import { Flex, Table, type TableRef, Text, theme } from '@/components';
import type { ColumnType } from '@/components/Table';
import { useDevToolsStorage } from '@/features/dev-tools/_hooks/local-storage';
import {
  type BudgetAndExecutionTableType,
  type BudgetAndExecutionTargetType,
  convertBudgetAndExecutionValue,
  getBudgetAndExecutionTableData,
} from '@/features/project/_utils/budget-and-execution';
import {
  useConsecutiveBudgets,
  useConsecutiveExecutions,
  useConsecutiveFiscalYears,
  useConsecutiveRequestAmounts,
} from '@/features/project/projects/detail/activities/_hooks/consecutive';
import { useExpandedRowKeys } from '@/hooks';
import { useLocaledAmount } from '@/providers';
import { FALLBACK_TEXT } from '@/utils';
import { type FC, useCallback, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { RecurringStatsSelector } from '../../../project-information/_components/RecurringStatsSelector';

type BudgetAndExecutionTableProps = {
  projectId: string;
  fiscalYear: number;
};

const NAME_COLUMN_WIDTH = 200;
const DATA_COLUMN_MIN_WIDTH = 140;

export const BudgetAndExecutionTable: FC<BudgetAndExecutionTableProps> = ({
  projectId,
  fiscalYear,
}) => {
  const { t } = useTranslation();
  const { unit } = useLocaledAmount();
  const { token } = theme.useToken();
  const tableRef = useRef<TableRef>(null);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const { expandedRowKeys, onExpand } = useExpandedRowKeys();
  const { devtoolState } = useDevToolsStorage();

  const { tableDataSource, years, tableYears } = getBudgetAndExecutionTableData(
    {
      consecutiveFiscalYears: useConsecutiveFiscalYears({ id: projectId }).data,
      consecutiveRequestAmounts: useConsecutiveRequestAmounts({ id: projectId })
        .data,
      consecutiveBudgets: useConsecutiveBudgets({ id: projectId }).data,
      consecutiveExecutions: useConsecutiveExecutions({ id: projectId }).data,
    },
  );

  const relatedCellColor = useMemo(
    () => ({
      background: '#F2F2F2',
      color: token.colorTextSecondary,
    }),
    [token.colorTextSecondary],
  );

  const dataColumnWidth = useMemo(() => {
    const containerWidth = tableContainerRef.current?.clientWidth;
    if (!containerWidth) {
      return DATA_COLUMN_MIN_WIDTH;
    }
    return Math.max(
      (containerWidth - NAME_COLUMN_WIDTH) / 8,
      DATA_COLUMN_MIN_WIDTH,
    );
  }, []);

  const handleInitialScroll = useCallback(() => {
    if (tableRef.current?.nativeElement && tableContainerRef.current) {
      tableContainerRef.current.scrollLeft +=
        tableRef.current.nativeElement.scrollWidth;
    }
  }, []);

  const getCellStyle = useCallback(
    (record: BudgetAndExecutionTableType, key: number) => {
      const currentFiscalYearIndex = years.findIndex(
        (year) => year === fiscalYear,
      );
      const priorFiscalYear =
        currentFiscalYearIndex > 0
          ? years[currentFiscalYearIndex - 1]
          : undefined;

      const isBudgetTypeRow = (
        [
          'initial',
          'supplementary',
          'carried-forward',
          'reserve',
        ] satisfies BudgetAndExecutionTargetType[]
      ).includes(record.type);

      const isCurrentYearBudget = key === fiscalYear;

      const isPriorYearReserveOrExecution =
        (
          ['reserve', 'execution'] satisfies BudgetAndExecutionTargetType[]
        ).includes(record.type) && key === priorFiscalYear;

      const isNextYearRequest =
        record.type === 'requested' && key === fiscalYear + 1;

      const isTotalRow = record.type === 'total';

      return {
        ...(isBudgetTypeRow && isCurrentYearBudget && relatedCellColor),
        ...(isPriorYearReserveOrExecution && relatedCellColor),
        ...(isNextYearRequest && relatedCellColor),
        ...(isTotalRow && {
          background: token.Table?.rowSelectedBg,
        }),
        width: dataColumnWidth,
      };
    },
    [
      years,
      fiscalYear,
      relatedCellColor,
      token.Table?.rowSelectedBg,
      dataColumnWidth,
    ],
  );

  const handleExpand = useCallback(
    (expanded: boolean, record: BudgetAndExecutionTableType) => {
      onExpand(expanded, record.name);
    },
    [onExpand],
  );

  const tableWidth = useMemo(
    () => ({
      minWidth: tableYears.length * DATA_COLUMN_MIN_WIDTH + NAME_COLUMN_WIDTH,
      width: tableYears.length * dataColumnWidth + NAME_COLUMN_WIDTH,
    }),
    [tableYears.length, dataColumnWidth],
  );

  useEffect(() => {
    handleInitialScroll();
  }, [handleInitialScroll]);

  const columns: ColumnType<BudgetAndExecutionTableType>[] = useMemo(
    () => [
      {
        title: '',
        key: 'name',
        dataIndex: 'name',
        rowScope: 'row',
        fixed: 'left',
        width: NAME_COLUMN_WIDTH,
        render: (value: string) => <Text text={value} />,
      },
      ...(tableYears.map((year) => ({
        title: <Text text={year} />,
        key: year,
        align: 'right',
        onCell: (record: BudgetAndExecutionTableType) => ({
          style: getCellStyle(record, year),
        }),
        render: (_, record) => {
          const target = record.valueByYear?.find((v) => v.year === year);
          if (!target) {
            return FALLBACK_TEXT;
          }
          return convertBudgetAndExecutionValue({
            value: target.value,
            name: record.name,
            hasNegativeValue: target.hasNegativeValue,
            isDebugMode: devtoolState?.isDebugMode,
            unit,
          });
        },
      })) satisfies ColumnType<BudgetAndExecutionTableType>[]),
    ],
    [tableYears, getCellStyle, unit, devtoolState?.isDebugMode],
  );

  return (
    <>
      <Flex justify="space-between" style={{ paddingBottom: 8 }}>
        <Text variant="h5" text={t('budget-and-execution-title')} />
        <RecurringStatsSelector />
      </Flex>

      <div
        ref={tableContainerRef}
        style={{ overflowX: 'scroll', backgroundColor: '#FAFAFA' }}
      >
        <Table
          enableSticky={false}
          aria-label="budget-and-execution-table"
          ref={tableRef}
          dataSource={tableDataSource}
          rowKey="name"
          style={tableWidth}
          columns={columns}
          expandable={{
            defaultExpandAllRows: true,
            expandedRowKeys,
            onExpand: handleExpand,
          }}
          pagination={false}
        />
      </div>
    </>
  );
};
